import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { GoogleAdGroup } from '../../types/seo';
import { exportGoogleAdsEditorCsv } from '../../utils/seoAIEngine';

interface GoogleAdsBridgeTabProps {
  adGroups: GoogleAdGroup[];
  onAddAdGroup: (group: GoogleAdGroup) => void;
  onUpdateAdGroup: (id: string, updated: Partial<GoogleAdGroup>) => void;
}

export default function GoogleAdsBridgeTab({
  adGroups,
  onAddAdGroup,
  onUpdateAdGroup,
}: GoogleAdsBridgeTabProps) {
  const [selectedAdGroupId, setSelectedAdGroupId] = useState<string>(adGroups[0]?.id || '');
  const [copied, setCopied] = useState(false);

  const activeAdGroup = adGroups.find((g) => g.id === selectedAdGroupId) || adGroups[0];

  const handleExportCsv = () => {
    const csvContent = exportGoogleAdsEditorCsv(adGroups);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `GoogleAds_Surat_RealEstate_Campaigns_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleHeadlineChange = (index: number, val: string) => {
    if (!activeAdGroup) return;
    const newHeadlines = [...activeAdGroup.headlines];
    newHeadlines[index] = val;
    onUpdateAdGroup(activeAdGroup.id, { headlines: newHeadlines });
  };

  const handleDescriptionChange = (index: number, val: string) => {
    if (!activeAdGroup) return;
    const newDescriptions = [...activeAdGroup.descriptions];
    newDescriptions[index] = val;
    onUpdateAdGroup(activeAdGroup.id, { descriptions: newDescriptions });
  };

  const handleCopyHeadlines = () => {
    if (!activeAdGroup) return;
    navigator.clipboard.writeText(activeAdGroup.headlines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      {/* Header Banner */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AdsClickIcon sx={{ color: '#9d6f32', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Google Ads Real Estate Search Campaign Bridge
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Translates Surat organic locality buyer queries into high-converting Responsive Search Ads with 1-Click Google Ads Editor export.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            sx={{
              bgcolor: '#9d6f32',
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(157,111,50,0.25)',
              '&:hover': { bgcolor: '#7a5525' },
            }}
          >
            1-Click Google Ads Editor CSV Export
          </Button>
        </Stack>
      </Paper>

      {/* Campaign Selector Chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        {adGroups.map((group) => {
          const isSelected = group.id === activeAdGroup?.id;
          return (
            <Chip
              key={group.id}
              label={group.name}
              onClick={() => setSelectedAdGroupId(group.id)}
              color={isSelected ? 'primary' : 'default'}
              variant={isSelected ? 'filled' : 'outlined'}
              sx={{
                fontWeight: isSelected ? 700 : 500,
                fontSize: 13,
                py: 2,
                bgcolor: isSelected ? '#9d6f32' : '#fff',
                borderColor: isSelected ? '#9d6f32' : '#ddd',
                color: isSelected ? '#fff' : '#333',
                cursor: 'pointer',
              }}
            />
          );
        })}
      </Box>

      {activeAdGroup && (
        <Grid container spacing={3}>
          {/* Left Column: Responsive Search Ads (15 Headlines & 4 Descriptions) */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff', mb: 3 }}>
              {/* Headlines Header */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Responsive Search Ad Headlines (Max 15, ≤ 30 chars each)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Dynamic insertion of Surat localities, ready possession status, and prices.
                  </Typography>
                </Box>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopyHeadlines} sx={{ color: '#9d6f32', fontWeight: 600 }}>
                  {copied ? 'Copied!' : 'Copy Headlines'}
                </Button>
              </Stack>

              <Grid container spacing={1.5}>
                {activeAdGroup.headlines.map((hl, idx) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Headline #${idx + 1} (${hl.length}/30)`}
                      value={hl}
                      onChange={(e) => handleHeadlineChange(idx, e.target.value)}
                      error={hl.length > 30}
                      helperText={hl.length > 30 ? 'Exceeds 30 char Google limit' : ''}
                      sx={{ bgcolor: '#fafafa' }}
                    />
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Descriptions Header */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Responsive Search Ad Descriptions (4 Descriptions, ≤ 90 chars each)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Highlights GujRERA approvals, zero brokerage, construction timeline (Ready vs Dec 2026).
              </Typography>

              <Stack spacing={1.5}>
                {activeAdGroup.descriptions.map((desc, idx) => (
                  <TextField
                    key={idx}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    label={`Description #${idx + 1} (${desc.length}/90 chars)`}
                    value={desc}
                    onChange={(e) => handleDescriptionChange(idx, e.target.value)}
                    error={desc.length > 90}
                    helperText={desc.length > 90 ? 'Exceeds 90 char Google limit' : ''}
                    sx={{ bgcolor: '#fafafa' }}
                  />
                ))}
              </Stack>

              <Divider sx={{ my: 3 }} />

              {/* Final URL & Extensions */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Final Destination URL & Sitelinks
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Final Landing Page URL"
                  value={activeAdGroup.finalUrl}
                  onChange={(e) => onUpdateAdGroup(activeAdGroup.id, { finalUrl: e.target.value })}
                  sx={{ mb: 2, bgcolor: '#fafafa' }}
                />

                <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', display: 'block', mb: 1 }}>
                  Callout Extensions ({activeAdGroup.calloutExtensions.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {activeAdGroup.calloutExtensions.map((callout, i) => (
                    <Chip key={i} label={`✓ ${callout}`} size="small" sx={{ bgcolor: '#f0f7f0', color: '#2e7d32', fontWeight: 600 }} />
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Google Search Ad Preview & Target Keywords */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Stack spacing={3}>
              {/* Google Ad Live Mockup */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#ffffff', borderColor: '#e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
                  Live Google Sponsored Search Ad Mockup
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#202124', fontSize: 12 }}>
                      Sponsored
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      · {activeAdGroup.finalUrl}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{
                      color: '#1a0dab',
                      fontWeight: 600,
                      fontSize: 18,
                      lineHeight: 1.3,
                      mt: 0.5,
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {activeAdGroup.headlines[0]} | {activeAdGroup.headlines[1]} | {activeAdGroup.headlines[2]}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#4d5156', fontSize: 13, mt: 0.8, lineHeight: 1.4 }}>
                    {activeAdGroup.descriptions[0]} {activeAdGroup.descriptions[1]}
                  </Typography>

                  {/* Sitelinks Mockup */}
                  <Grid container spacing={1.5} sx={{ mt: 1 }}>
                    {activeAdGroup.sitelinks.slice(0, 2).map((st, i) => (
                      <Grid size={{ xs: 6 }} key={i}>
                        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f9f9f9', borderRadius: 1.5 }}>
                          <Typography variant="caption" sx={{ color: '#1a0dab', fontWeight: 700, display: 'block' }}>
                            {st.title}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: 10, color: '#555' }}>
                            {st.desc1}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>

              {/* Target & Negative Keywords */}
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Ad Group Target Keywords ({activeAdGroup.targetKeywords.length})
                </Typography>
                <Stack spacing={0.8} sx={{ mb: 2 }}>
                  {activeAdGroup.targetKeywords.map((kw, i) => (
                    <Box key={i} sx={{ p: 0.8, bgcolor: '#f7f7f7', borderRadius: 1, fontSize: 12, fontFamily: 'monospace' }}>
                      {kw}
                    </Box>
                  ))}
                </Stack>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#d32f2f' }}>
                  Ad Group Negative Exclusions ({activeAdGroup.negativeKeywords.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {activeAdGroup.negativeKeywords.map((neg, i) => (
                    <Chip key={i} label={`-${neg}`} size="small" color="error" variant="outlined" sx={{ fontSize: 11 }} />
                  ))}
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
